import Recaptcha from '@/utils/recaptcha.ts'
import {
  SESSION_STORAGE_KEY_LAST_ACTIVITY,
  SESSION_STORAGE_KEY_PV_COUNT,
  SESSION_STORAGE_KEY_START,
  SESSION_TIMEOUT_MS,
} from '@/constants'

/**
 * OneTrust・Cookiebot関連
 */

declare global {
  interface Window {
    /**
     * OneTrust読み込みで自動でセットされる
     * ユーザーが同意したCookieのIDが入る（例: 'C0001,C0002,'）
     */
    OnetrustActiveGroups?: string;
    gtag?: (event: string, action: string, params: Record<string, any>) => void;
    /**
     * GA無効化フラグ
     * ${string} にはGAプロパティID（例：G-XXXXXXXXXX）が入る（sendTo）
     */
    [key: `ga-disable-${string}`]: boolean | undefined;
    /**
     * Cookiebot読み込みで自動でセットされるオブジェクト
     * consent内にカテゴリごとのCookie同意情報が入る（true:同意、false:拒否）
     */
    Cookiebot?: {
      consent: {
        necessary: boolean;
        preferences: boolean;
        statistics: boolean;
        marketing: boolean;
      };
    };
  }
}

/**
 * OneTrustの設定
 */
interface OneTrustConfig {
  /**
  * OneTrustを有効とするか 有効にした場合は同意がなければ送信しない
  * 有効：true、無効：false
  */
  enabled: boolean;
  /**
  * Analyticsを同意したとする対象のID 例: "C0002"
  */
  consentId: string;
}

/**
 * Cookiebotの設定
 *
 * Cookieカテゴリは設定せず'statistics'（統計）固定でここでは指定しない
 * GAの用途は統計であり'statistics'に分類が妥当であるため
 */
interface CookiebotConfig {
  /**
  * Cookiebotを有効とするか 有効にした場合は同意がなければ送信しない
  * 有効：true、無効：false
  */
  enabled: boolean;
}

/**
 * Analyticsの詳細設定
 */
interface AnalyticsConfig {
  onetrust?: OneTrustConfig;
  cookiebot?: CookiebotConfig;
}

export class OneTrust {

  /** 同意対象のID */
  public consentId: string

  /** イベント名 候補はイベント設計を参照 */
  public name: string

  /** 送信するもの、イベントごとのパラメータ */
  public params: any

  /** 送信先、GAのプロパティID */
  private sendTo: string

  /** デバッグログの出力有無 */
  public debug: boolean

  /**
   * onetrustから渡される現在同意済みのイベント名
   * 例: ",C0001,C0002,C0004,C0003,"
   */
  public activeGroups: string

  /**
   * @param {string} consentId
   * @param {string} name
   * @param {Object} params 送信するもの、イベントごとのパラメータ
   * @param {string} sendTo 送信先、GAのプロパティID
   * @param {boolean} debug イベントのパラメータをログにだす場合はtrue
   */
  constructor(consentId: string, name: string, params: any, sendTo: string, debug = false) {
    this.consentId = consentId
    this.name = name
    this.params = params
    this.sendTo = sendTo
    this.debug = debug
  }

  /**
   * OneTrustの初期化状態と同意がされているかをチェックし
   * 同意済みの場合は送信し、同意が取れる前に送られたイベントは同意後も処理されない
   *
   * NOTE: 同意のcallbackを取るためにOptanonWrapper関数でOneTrustGroupsUpdatedを手動で発火させる必要あり。
   *
   * ```html
   * <script type="text/javascript">
   * function OptanonWrapper() {
   *  var event = new Event('OneTrustGroupsUpdated');
   *  document.dispatchEvent(event);
   * }
   * </script>
   * ```
   * OptanonWrapperは読み込み時、保存時に実行される関数
   * 参考:
   * https://my.onetrust.com/s/article/UUID-29158b4e-22f6-0067-aa36-94f3b8cf3561?language=en_US
   * https://my.onetrust.com/s/article/UUID-730ad441-6c4d-7877-7f85-36f1e801e8ca?language=ja#idm46636330750352
   */
  sendEventOnConsent() {
    // NOTE: 同意の取れている場合は OnetrustActiveGroups に文字列が入る
    //       例: ",C0001,C0002,C0004,C0003,"
    if (typeof window.OnetrustActiveGroups !== 'undefined') {
      // 同意の取れている項目の確認
      this.activeGroups = window.OnetrustActiveGroups || ''
      if (this.activeGroups.includes(this.consentId)) {
        // gtagを有効化
        window[`ga-disable-${this.sendTo}`] = false
        // 対象IDの同意が確認できたら送信する
        this.#sendEvent()
        this.debugLog(`✅ Event has been successfully sent (OneTrust)`, `ActiveGroups: ${this.activeGroups}`, this.name, this.params)
        return true
      } else {
        this.debugLog(`❌ Failed to send '${this.name}' event. code ${this.consentId} was not consent upon.`, `ActiveGroups: ${this.activeGroups}`)
        // gtagを無効化（標準イベントも含めて停止）
        window[`ga-disable-${this.sendTo}`] = true
        // 同意されていない場合、引き続き更新を監視する
        document.addEventListener('OneTrustGroupsUpdated', ()=> this.sendEventOnConsent(), { once: true })
        return false
      }
    } else {
      // ページの読み込み直後はOnetrustActiveGroupsが未定義の状態なので更新イベントを監視して再度処理する
      this.debugLog("⏳ OneTrust status is undefined. Waiting for load completion...")
      // OneTrustの更新イベントを監視
      document.addEventListener('OneTrustGroupsUpdated', ()=> this.sendEventOnConsent(), { once: true })
      return false
    }
  }

  #sendEvent() {
    try {
      window.gtag('event', this.name, this.params)
    } catch (e) {
      console.warn(e)
    }
  }

  debugLog(...messages: string[]) {
    if(this.debug) {
      console.debug(...messages)
    }
  }
}

export class Cookiebot {
  /** イベント名 候補はイベント設計を参照 */
  public name: string

  /** 送信するもの、イベントごとのパラメータ */
  public params: any

  /** 送信先、GAのプロパティID */
  private sendTo: string

  /** デバッグログの出力有無 */
  public debug: boolean

  /**
   * @param {string} name
   * @param {Object} params 送信するもの、イベントごとのパラメータ
   * @param {string} sendTo 送信先、GAのプロパティID
   * @param {boolean} debug イベントのパラメータをログにだす場合はtrue
   */
  constructor(name: string, params: any, sendTo: string, debug = false) {
    this.name = name
    this.params = params
    this.sendTo = sendTo
    this.debug = debug
  }

  /**
   * Cookiebotの初期化状態と同意がされているかをチェックし
   * 同意済みの場合は送信し、同意が取れる前に送られたイベントは同意後も処理されない
   *
   * NOTE:
   * Cookiebotの場合は、初期化時のイベント'CookiebotOnConsentReady'が用意されているため
   * これを使えばよく、OneTrustのようなcallback関数は不要
   *
   * 参考：
   * https://www.cookiebot.com/en/developer/
   *
   */
  sendEventOnConsent() {
    if (window.Cookiebot?.consent && typeof window.Cookiebot.consent.statistics !== 'undefined') {
      // statisticsのみ同意が取れていればOK
      if (window.Cookiebot.consent.statistics) {
        // gtagを有効化
        window[`ga-disable-${this.sendTo}`] = false
        // 対象IDの同意が確認できたら送信する
        this.#sendEvent()
        this.debugLog(`✅ Event has been successfully sent (Cookiebot)`, this.name, this.params)
        return true
      } else {
        this.debugLog(`❌ Failed to send '${this.name}' event because Cookiebot statistics consent was not given.`)
        // gtagを無効化（標準イベントも含めて停止）
        window[`ga-disable-${this.sendTo}`] = true
        // 同意されていない場合、引き続き更新を監視する
        window.addEventListener('CookiebotOnAccept', () => this.sendEventOnConsent(), { once: true })
        return false
      }
    } else {
      // ページの読み込み直後はCookiebotが未定義の可能性があるので、イベントを監視して再度処理する
      this.debugLog("⏳ Cookiebot is undefined. Waiting for load completion...")
      // Cookiebotが初期化されて同意状態が取得可能になったタイミングで発火
      window.addEventListener('CookiebotOnConsentReady', () => this.sendEventOnConsent(), { once: true })
      return false
    }
  }

  #sendEvent() {
    try {
      window.gtag('event', this.name, this.params)
    } catch (e) {
      console.warn(e)
    }
  }

  debugLog(...messages: string[]) {
    if (this.debug) {
      console.debug(...messages)
    }
  }
}

/**
 * Google Analyticsのイベント送信関連の制御用
 *
 * 例外は自動的に送信する
 */
export class Analytics {
  /**
   * true: デバッグモード、送信はしないが、送信値をログには出したいときに使う
   */
  public debug: boolean

  /**
   * 送信しない場合はtrue、社内で確認する場合に使う
   */
  private ignore: boolean

  /**
   * 送信先、GAのプロパティID
   */
  private sendTo: string

  /**
   * 最後に送信したパラメータ
   * 連続での重複送信防止用
   */
  private latestQuery: string = ''

  /**
   * 送信前に実行する処理
   */
  private onBeforeSend: () => void = () => {}

  /**
   * 送信するパラメータの初期値、常に送信するものを格納する
   */
  private context: {[key: string]: string | number | boolean} = {}

  /**
   * 過剰なイベント送信の抑制用
   * {送信したイベント名: { イベントの識別ID: {} }}
   */
  private cache: {[EventName: string]: {[EventUniqueId: string]: object }} = {}

  /**
   * jsonで記述するAnalyticsの詳細設定
   */
  private config: AnalyticsConfig

  /**
   * @param sendTo 送信先、GAのプロパティID
   * @param debug イベントのパラメータをログにだす場合はtrue
   * @param ignore 送信しない場合はtrue、社内で確認する場合に使う
   * @param {AnalyticsConfig} config jsonで記述するAnalyticsの詳細設定
   */
  constructor(sendTo: string, debug = false, ignore = false, config: AnalyticsConfig = {}) {
    this.debug = debug
    this.ignore = ignore
    this.sendTo = sendTo
    this.config = config

    // SLV3A-4690 端末番号などが含まれないよう数字は間引く
    this.context['UserAgent'] = navigator.userAgent
      ? navigator.userAgent.replace(/\d+/g, '*')
      : 'unknown'

    this.context['CurrentLocationStatus'] = false

    // セッション開始時刻の初期化・ページ表示回数インクリメント
    // localStorageが使えない環境ではnullを設定する
    try {
      const didReset = this._resetSessionIfTimedOut()
      if (!localStorage.getItem(SESSION_STORAGE_KEY_START)) {
        localStorage.setItem(SESSION_STORAGE_KEY_START, String(Date.now()))
      }
      if (!didReset) {
        const pvCount = parseInt(localStorage.getItem(SESSION_STORAGE_KEY_PV_COUNT) || '0') + 1
        localStorage.setItem(SESSION_STORAGE_KEY_PV_COUNT, String(pvCount))
      }
      this.context['SessionPageViewCount'] = parseInt(localStorage.getItem(SESSION_STORAGE_KEY_PV_COUNT) || '0')
      localStorage.setItem(SESSION_STORAGE_KEY_LAST_ACTIVITY, String(Date.now()))
    } catch {
      // localStorage使用不可環境ではnullを設定する
      // SessionElapsedTimeはsend()内で同様にnullが設定される
      this.context['SessionPageViewCount'] = null
    }

    // 補足できてない例外は自動送信
    window.addEventListener('error', (e: ErrorEvent) => {
      console.warn('onError', e.type) // errorログにはでるので、ここは最低限でいい

      this.send('ExceptionThrowed', {
        ErrorMessage: e.message || e.error?.message || e.type,
        ErrorStack: e.error?.stack,
      })
    })

    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      console.warn('onError', e.type) // errorログにはでるので、ここは最低限でいい

      this.send('ExceptionThrowed', {
        ErrorMessage: e.reason?.message || e.type,
        ErrorStack: e.reason?.stack,
      })
    })

    if (this.config.onetrust?.enabled) {
      const consentId = this.config.onetrust.consentId || 'C0002'
      document.addEventListener('OneTrustGroupsUpdated', () => this.applyOneTrustConsentToGtag(consentId, this.sendTo))
    }

    // Cookiebot利用の場合
    if (this.config.cookiebot?.enabled) {
      window.addEventListener('CookiebotOnAccept', () => this.applyCookiebotConsentToGtag(this.sendTo))
      window.addEventListener('CookiebotOnDecline', () => this.applyCookiebotConsentToGtag(this.sendTo))
    }
  }

  /**
   * OneTrustの同意・拒否によりgtagを有効化・無効化する
   * 無効化すると、標準イベントを含む全イベントの送信がされない
   *
   * ドキュメントURL
   * https://developers.google.com/tag-platform/security/guides/privacy?hl=ja#turn-off-analytics
   *
   * @param {string} consentId OneTrustの同意対象のID（例：C0002）
   * @param {string} sendTo GAイベントの送信先（GAのプロパティID）
  */
  applyOneTrustConsentToGtag(consentId: string, sendTo: string) {
    if (typeof window.OnetrustActiveGroups === 'undefined') {
      return
    }
    const activeGroups = window.OnetrustActiveGroups || ''
    if (activeGroups.includes(consentId)) {
      // 同意の場合はgtagを有効化
      window[`ga-disable-${sendTo}`] = false
    } else {
      // 拒否の場合はgtagを無効化
      window[`ga-disable-${sendTo}`] = true
      console.info('gtag disabled')
    }
  }

  /**
   * Cookiebotの同意・拒否によりgtagを有効化・無効化する
   * 無効化すると、標準イベントを含む全イベントの送信がされない
   *
   * @param {string} sendTo GAイベントの送信先（GAのプロパティID）
  */
  applyCookiebotConsentToGtag(sendTo: string) {
    if (!window.Cookiebot || !window.Cookiebot.consent || typeof window.Cookiebot.consent.statistics === 'undefined') {
      return
    }
    if (window.Cookiebot.consent.statistics) {
      // 同意の場合はgtagを有効化
      window[`ga-disable-${sendTo}`] = false
    } else {
      // 拒否の場合はgtagを無効化
      window[`ga-disable-${sendTo}`] = true
      console.info('gtag disabled')
    }
  }

  /**
   * 送信前に実行する処理を設定
   * contextの最新化を仕込む運用を想定
   * @param callback 送信前に実行する処理
   */
  setBeforeSend(callback: () => object) {
    this.onBeforeSend = callback
  }

  /**
   * キャッシュをリセット
   */
  resetCache() {
    this.cache = {}
  }

  /**
   * GA4のセッションタイムアウト（30分無操作）に合わせてセッション値をリセット。
   * 最後のイベント送信から30分以上経過していたらセッション開始時刻・PVカウントを初期化する。
   * @returns リセットが発生した場合true
   */
  private _resetSessionIfTimedOut(): boolean {
    const lastActivity = parseInt(localStorage.getItem(SESSION_STORAGE_KEY_LAST_ACTIVITY) || '0')
    if (lastActivity > 0 && (Date.now() - lastActivity) > SESSION_TIMEOUT_MS) {
      localStorage.setItem(SESSION_STORAGE_KEY_START, String(Date.now()))
      localStorage.setItem(SESSION_STORAGE_KEY_PV_COUNT, '1')
      return true
    }
    return false
  }

  /**
   * GAへのイベント送信
   * 肥大化したらクラス化も検討
   *
   * window.google_analyticsが未定義なら送信しない
   *
   * @param name イベント名 候補はイベント設計を参照
   * @param params 送信するもの、イベントごとのパラメータ
   * @param cacheId 送信の重複を防ぐためのID、同じIDでの連続送信は抑制される
   */
  send(name: string, params: any, cacheId: string = null){
    if (!this.debug && !(window as any).gtag) {
      /**
       * gtagが定義されていない場合は送信しない、
       * NOTE:
       *  - debugモードの時は、gtag未定義でも送信直前まで進ませる
       *  - 2.1現在、gtagの定義は管理画面のデザイン設定から行う（ソースコード上には存在しない）
       */
      return
    }

    if (!this.sendTo) {
      if (this.debug) console.debug('target is not set')
      return
    }

    this.onBeforeSend()

    // セッション経過時間・ページ表示回数を最新化
    // localStorageが使えない環境ではnullを設定する
    try {
      if (this._resetSessionIfTimedOut()) {
        this.cache = {}
        this.latestQuery = ''
      }
      const sessionStart = parseInt(localStorage.getItem(SESSION_STORAGE_KEY_START) || '0')
      this.context['SessionElapsedTime'] = Math.floor((Date.now() - sessionStart) / 1000)
      // セッション内ページ表示回数を最新化（別タブでの更新を反映）
      this.context['SessionPageViewCount'] = parseInt(localStorage.getItem(SESSION_STORAGE_KEY_PV_COUNT) || '0')
      localStorage.setItem(SESSION_STORAGE_KEY_LAST_ACTIVITY, String(Date.now()))
    } catch {
      this.context['SessionElapsedTime'] = null
      this.context['SessionPageViewCount'] = null
    }

    // reCAPTCHAが有効な場合、結果をコンテキストに追加
    if (Recaptcha.isEnabled) {      
      const recaptchaContext = Recaptcha.getAnalyticsContextValues()
      this.context['RecaptchaCachedVerify'] = recaptchaContext
    }

    const base = JSON.parse(JSON.stringify(this.context))
    base.send_to = this.sendTo
    base.value = 1

    const fixedParams = Object.assign(base, params)

    // 過剰に呼ばれる処理があるときに二重送信にならないように
    const query = `${name}${JSON.stringify(fixedParams)}`
    if (this.latestQuery == query) {
      if (this.debug) console.debug('skip double send', name, fixedParams)
      return
    }

    if (this.ignore) {
      if (this.debug) console.debug('not send event')
      return
    }

    if (this.cache[name] && cacheId && this.cache[name][cacheId]) {
      if (this.debug) console.debug('cached event, skip sent event', name, cacheId)
      return
    }

    /** イベントの送信 */
    try {
      let success = false
      if(this.config.onetrust?.enabled) {
        // OneTrustが有効であれば、同意状態を確認してから送信
        const consentId = this.config.onetrust.consentId || 'C0002'
        if(this.debug) console.debug(`Sending event on OneTrust`, name, fixedParams)
        const onetrust = new OneTrust(consentId, name, fixedParams, this.sendTo, this.debug)
        success = onetrust.sendEventOnConsent()

      // OneTrustとCookiebotが両方有効のケースは想定していない
      } else if (this.config.cookiebot?.enabled) {
      // Cookiebotが有効であれば、同意状態を確認してから送信
        if (this.debug) console.debug(`Sending event on Cookiebot`, name, fixedParams)
        const cookiebot = new Cookiebot(name, fixedParams, this.sendTo, this.debug)
        success = cookiebot.sendEventOnConsent()

      } else if (window.gtag) {
        // デバッグモードの時はgoogle_analytics未定義でもここにくる

        // デフォルトでgtag無効にしている場合にのみ、gtagの有効化が必要
        // （OneTrust・Cookiebot無効だがデフォルトでgtag一律無効にしている特定国のマップなど特殊なケースを想定）
        if (window[`ga-disable-${this.sendTo}`]) {
          window[`ga-disable-${this.sendTo}`] = false
        }

        window.gtag('event', name, fixedParams)
        success = true
      } else {
        if (this.debug) console.debug('gtag is not defined', name, fixedParams)
      }
      if(!success) {
        // 送信が成功しなかった場合は処理を停止
        return
      }
    } catch (e) {
      console.warn(e)
    }

    if (this.debug) console.debug('send event', name, fixedParams)

    // キャッシュ記録
    if (!this.cache[name]) {
      this.cache[name] = {}
    }

    if (this.debug) console.debug('cache sent event', name, cacheId)
    this.cache[name][cacheId] = {}

    this.latestQuery = query
  }
}